import { useEffect, useState } from 'react'
import { gccTaxonomyTruncationMessage } from '@shared/gcc/uploads'
import { useApi } from '../contexts/ApiContext'

type UploadedFile = { base64: string; name: string }

interface Props {
  files: UploadedFile[]
}

/**
 * Step 1 warning when the uploaded taxonomy text is longer than the Step 0
 * prompt reads (WI 2888): how much is read and what is left out, before
 * generating. Text is extracted on the server, so only clients that implement
 * `checkGccTaxonomy` (web) show it. It never blocks Next, and a failed check
 * shows nothing: the run itself reports an unreadable file.
 */
export default function GccTaxonomySizeNotice({ files }: Props) {
  const api = useApi()
  // Tied to the list it was checked for, so an answer for an older list is never shown
  const [result, setResult] = useState<{ files: UploadedFile[]; message: string | null } | null>(null)

  useEffect(() => {
    if (!api.checkGccTaxonomy || files.length === 0) return
    let current = true
    api.checkGccTaxonomy(files).then(
      (read) => { if (current) setResult({ files, message: gccTaxonomyTruncationMessage(read) }) },
      () => { /* no warning: generation reports the error */ },
    )
    return () => { current = false }
  }, [api, files])

  const message = result?.files === files ? result.message : null
  if (!message) return null

  return (
    <div role="status" className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
      <p className="font-semibold">Part of the taxonomy will be left out</p>
      <p className="mt-0.5">{message}</p>
    </div>
  )
}
